import type { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";

/**
 * Counts the number of active filters that differ from their default values.
 * Used to display the filter count badge in the mobile analytics header.
 */
export function getActiveFilterCount(
  filters: IExperienceDashboardFilters
): number {
  let count = 0;

  // Device type filter (default: "any")
  if (filters.deviceType !== "any") {
    count++;
  }

  // Visitor type filter (default: "any")
  if (filters.visitorType !== "any") {
    count++;
  }

  // Channels filter (default: empty array)
  if (filters.channels.length > 0) {
    count++;
  }

  // Date range filter (default: both null)
  if (filters.startTime !== null || filters.endTime !== null) {
    count++;
  }

  // Country codes filter (default: empty array)
  if (filters.countryCodes.length > 0) {
    count++;
  }

  // Custom events filter (default: empty array)
  if (filters.customEvents.length > 0) {
    count++;
  }

  // Shipping methods filter (default: empty array)
  if (filters.shippingMethods.length > 0) {
    count++;
  }

  // URL param filter
  if (
    filters.urlParam.queryParam !== null ||
    filters.urlParam.queryParamValue !== null
  ) {
    count++;
  }

  // Landing page filter
  if (filters.landingPage?.landingPageUrlPath !== null) {
    count++;
  }

  // User behavior filters
  const userBehavior = filters.userBehavior;
  if (
    userBehavior?.viewedCheckout !== null ||
    userBehavior?.addedToCart !== null ||
    userBehavior?.viewedCollectionPage !== null ||
    userBehavior?.viewedProductPage !== null
  ) {
    count++;
  }

  // Revenue filters (min/max net revenue or shipping revenue)
  if (
    filters.minNetRevenue !== null ||
    filters.maxNetRevenue !== null ||
    filters.minShippingRevenue !== null ||
    filters.maxShippingRevenue !== null
  ) {
    count++;
  }

  // Checkout stage filter (default: "any")
  if (filters.reachedCheckoutStage && filters.reachedCheckoutStage !== "any") {
    count++;
  }

  // Analytics view type filter (default: "sitewide_orders")
  if (
    filters.analyticsViewType &&
    filters.analyticsViewType !== "sitewide_orders"
  ) {
    count++;
  }

  // Selected analytics products filter (default: empty array)
  if (
    filters.selectedAnalyticsProducts &&
    filters.selectedAnalyticsProducts.length > 0
  ) {
    count++;
  }

  // Z-Score filter (default: isZScoreEnabled = true, maxZScore = null)
  // Count as active if disabled OR if a custom max value is set
  if (filters.isZScoreEnabled === false) {
    count++;
  }

  return count;
}
